'use client';
import { getAllStores } from '@api/store';
import { TAddress, TCartItem, TOrder, TStore, TStoreCharge } from '@components/types';
import DeliverySlots from '@components/widget/deliverySlots';
import OrderInstructions from '@components/widget/orderInstructions';
import { humanizeCurrency } from '@core/utils';
import { cartDetailsState, cartState, isLoggedIn } from '@recoil/atoms';
import { Loader } from 'lucide-react';
import { usePathname } from 'next/navigation';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

type Props = {
    placeOrder?: string;
    btnName?: string;
    actionFun?: Function;
    isHidden?: boolean;
    isShowTaxes?: boolean;
    orderDetails?: TOrder[];
    isLoading?: boolean;
    store?: TStore
    deliverableStores?: number[]
    selectedAddress?: TAddress
    paymentBreakup?: any
    orderInstruction?: any,
    setOrderInstruction?: Function
};

export const SPACartDetails = ({
    placeOrder = '/cart/paymentmethod',
    isLoading = false,
    btnName,
    actionFun,
    isHidden = false,
    isShowTaxes = false,
    orderDetails,
    store,
    deliverableStores,
    selectedAddress,
    paymentBreakup,
    orderInstruction,
    setOrderInstruction
}: Props) => {
    const { discountedTotal, savings } = useRecoilValue(cartDetailsState);
    const cart = useRecoilValue(cartState);
    const cartValues = Array.from(cart.values());
    const hasDeliveryItem = cartValues.some((item) => item.deliverable);
    const pathname = usePathname()
    const deliverableCart = cartValues?.filter((item) => { return item?.deliverable })
    const [stores, setStores] = useState<any>({});
    const [storeLoading, setStoreLoading] = useState(false);
    const [isDelivery, setIsDelivery] = useState(false);
    const minimumOrderValue = Number(store?.storemeta?.settings?.minimum_order_value || 0);
    const totalAmount = Number(paymentBreakup?.summary?.total_payable || discountedTotal || 0);
    const isBelowMinimumOrder = minimumOrderValue > 0 && totalAmount < minimumOrderValue;
    const minimumOrderRemaining = Math.max(minimumOrderValue - totalAmount, 0);
    const hasUndeliverableItems =
        pathname === "/cart/selectaddress" &&
        cartValues?.map((i) => i?.inventory?.store_id).every((item) => deliverableStores?.includes(item) === false);
    const isProceedDisabled = isLoading || isBelowMinimumOrder || hasUndeliverableItems;

    // Fetch store information when cart changes
    const fetchStoreInformation = async (storeIds: any[]) => {
        setStoreLoading(true);
        try {
            const storeInfo = await getAllStores(storeIds);
            const storeMap: any = {};
            storeInfo.forEach((store: TStore) => {
                storeMap[store?.id] = store;
            });
            setStores(storeMap);
        } catch (error) {
            console.error("Failed to fetch store information:", error);
        } finally {
            setStoreLoading(false);
        }
    };

    const getStoreMetaForItem = (cartItem: TCartItem) => {
        if (!cartItem?.inventory?.store_id) return null;
        return stores[cartItem?.inventory?.store_id];
    };

    useEffect(() => {
        const hasDeliverableItems = cartValues.some((item) => item.deliverable);
        setIsDelivery(hasDeliverableItems);
    }, [cartValues]);

    useEffect(() => {
        if (cartValues?.length > 0) {
            const uniqueStoreIds = Array?.from(new Set(cartValues?.map(item => item?.inventory?.store_id)));
            fetchStoreInformation(uniqueStoreIds);
        }
    }, [cartValues?.map(item => item?.inventory?.store_id)?.join(',')]);


    const totalTax = cartValues.reduce(
        (prevTax, currentTax) =>
            prevTax +
            (getStoreMetaForItem(currentTax)?.storemeta?.functions?.gst_billing ?
                (currentTax?.inventory?.tax_exclusive
                    ? (((currentTax?.inventory?.price * currentTax.inventory?.tax_rate) / 100) + (currentTax?.addon?.reduce((prevAdd: number, addon) => prevAdd + (addon?.inventory?.tax_exclusive ? ((addon?.price * addon?.inventory?.tax_rate) / 100 || 0) : 0), 0) || 0)
                    )
                    * currentTax.quantity
                    : (((currentTax.inventory?.tax_rate *
                        (currentTax?.inventory?.price /
                            (1 + currentTax.inventory?.tax_rate / 100))) /
                        100) + (currentTax?.addon?.reduce((prevAdd: number, addon) => prevAdd + (addon?.inventory?.tax_exclusive ? ((addon?.price * addon?.inventory?.tax_rate) / 100 || 0) : 0), 0) || 0))
                    *
                    currentTax.quantity) : 0),
        0
    );
    let tableInfo =
        typeof window !== 'undefined'
            ? JSON.parse(sessionStorage.getItem('tableInfo'))
            : null;

    const goToOrderPage = () => {
        if (
            (tableInfo?.table !== null && tableInfo?.table !== undefined) ||
            !hasDeliveryItem
        ) {
            Router.push(placeOrder);
        } else {
            isLoggedIn ? Router.push('/cart/selectaddress') : Router.push('/login');
        }
    };

    return (
        <div className="flex flex-col relative lt-sm:grow w-full font-manrope gap-2">
            {(store?.storemeta?.settings?.schedule_delivery && orderInstruction) && <DeliverySlots store={store} orderInstructions={orderInstruction} setOrderInstructions={setOrderInstruction} />}
            {(orderInstruction) && <OrderInstructions orderInstructions={orderInstruction} setOrderInstructions={setOrderInstruction} />}
            {
                <div
                    className={`flex flex-col gap-2 w-full lt-sm:grow sticky top-5 ${isHidden ? 'justify-end' : 'justify-between'
                        }`}
                >
                    <div
                        className={`bg-white px-4 py-3 rounded-2xl md:block shadow-sm border border-neutral-200/70 ${isHidden && 'hidden'
                            }`}
                    >
                        {<div className="flex flex-col gap-2 justify-between">
                            <p className="font-manrope font-semibold text-neutral-800 pb-2 text-base border-b border-neutral-100">
                                Order total
                            </p>
                            <div className="flex flex-col gap-1.5">
                                <div className="flex flex-row justify-between gap-2">
                                    <span className="font-manrope text-sm font-medium text-neutral-500">
                                        Price
                                    </span>
                                    <span className="font-manrope text-sm font-semibold text-neutral-800">
                                        {humanizeCurrency(paymentBreakup?.summary?.order_item_net_amount || 0)}
                                    </span>
                                </div>
                                {paymentBreakup?.summary?.actual_discount > 0 && (
                                    <div className="flex flex-row justify-between gap-2">
                                        <span className="text-sm font-manrope text-neutral-500">
                                            Discount
                                        </span>
                                        <span className="text-sm font-semibold text-success-500">
                                            {' '}
                                            -{humanizeCurrency(paymentBreakup?.summary?.actual_discount || 0)}
                                        </span>
                                    </div>
                                )}
                                {(isShowTaxes && paymentBreakup?.summary?.total_tax > 0) && (
                                    <div className="flex flex-row justify-between gap-2">
                                        <span className="font-manrope text-sm font-medium text-neutral-500">
                                            Total tax (CGST + SGST){' '}
                                        </span>
                                        <span className="font-manrope text-sm font-semibold text-neutral-800">
                                            {humanizeCurrency(paymentBreakup?.summary?.total_tax || 0)}
                                        </span>
                                    </div>
                                )}
                                {paymentBreakup?.summary?.charges_net_amount > 0 && (
                                    <div className="flex flex-row justify-between gap-2">
                                        <span className="font-manrope text-sm font-medium text-neutral-500">
                                            Charges
                                        </span>
                                        <span className="font-manrope text-sm font-semibold text-neutral-800">
                                            {humanizeCurrency(paymentBreakup?.summary?.charges_net_amount || 0)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-row justify-between border-t border-neutral-100 pt-2 gap-2">
                                <span className="font-manrope text-base font-semibold text-neutral-700">
                                    Total amount
                                </span>
                                <span className="text-base font-bold text-business-500">
                                    {humanizeCurrency(paymentBreakup?.summary?.total_payable || 0)}
                                </span>
                            </div>
                        </div>}
                    </div>
                    <div className={`py-2 ${isHidden && ''}`}>
                        {
                            isBelowMinimumOrder &&
                            <span className="mb-2 flex items-center justify-center gap-1.5 text-center text-xs font-semibold text-error-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-error-400" />
                                <span>
                                    Add {humanizeCurrency(minimumOrderRemaining)} more items. Minimum order value is {humanizeCurrency(minimumOrderValue)}.
                                </span>
                            </span>
                        }
                        <button
                            disabled={isProceedDisabled}
                            onClick={() => {
                                actionFun ? actionFun() : goToOrderPage();
                            }}
                            className={`flex items-center justify-center gap-2 bg-brand-500 shadow-sm font-semibold text-sm text-neutral-50 px-1 md:px-2 py-3 rounded-xl w-full font-manrope disabled:opacity-60 hover:brightness-110 transition-all ${isProceedDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                            {isLoading && <Loader color='#fff' className="w-5 h-5 text-brand-500 animate-spin" />}
                            <span>{btnName ? btnName : 'PLACE ORDER'}</span>
                        </button>
                        {
                            hasUndeliverableItems &&
                            <span className="flex justify-center text-error-500 text-sm pt-1">Please remove undeliverable items from your cart.</span>
                        }
                    </div>
                </div>
            }
        </div >
    );
};
